import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { invalidateOrganizationSettings } from '../hooks/useOrganizationSettings'
import { setActiveOrganizationId } from '../utils/activeOrganization'
import { dataCenterFromRegionParam, setSelectedDC } from '../config'
import { setSessionToken } from '../auth/session'
import {
  acceptOrgInvite,
  persistOrgInviteTokenFromURL,
} from '../services/orgInvite'

/** Processes `?invite=` on any route: persist when logged out, accept when logged in. */
export default function OrgInviteParamHandler() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated, isLoading, checkAuth } = useAuth()
  const { showNotification } = useNotification()
  const navigate = useNavigate()

  useEffect(() => {
    const token = searchParams.get('invite')?.trim()
    if (!token || isLoading) return

    const region = searchParams.get('region')?.trim()
    if (region) {
      const dc = dataCenterFromRegionParam(region)
      if (dc) setSelectedDC(dc)
    }

    const clearInviteParam = () => {
      const next = new URLSearchParams(searchParams)
      next.delete('invite')
      setSearchParams(next, { replace: true })
    }

    if (!isAuthenticated) {
      persistOrgInviteTokenFromURL(searchParams)
      clearInviteParam()
      if (window.location.pathname !== '/join' && window.location.pathname !== '/login') {
        const joinParams = new URLSearchParams({ invite: token })
        if (region) joinParams.set('region', region)
        navigate(`/join?${joinParams.toString()}`, { replace: true })
      }
      return
    }

    let cancelled = false
    const run = async () => {
      try {
        const accepted = await acceptOrgInvite(token)
        if (cancelled) return
        clearInviteParam()
        localStorage.removeItem('veilstream_pending_org_invite')

        if (accepted.token) {
          setSessionToken(accepted.token)
        }
        if (accepted.organization_id) {
          setActiveOrganizationId(accepted.organization_id)
        }
        await checkAuth()
        invalidateOrganizationSettings()
        const name = accepted.organization_name || 'the organization'
        showNotification(
          accepted.already_member ? `You are already a member of ${name}` : `You joined ${name}`,
          'success',
        )
        if (window.location.pathname === '/login' || window.location.pathname === '/join') {
          navigate('/projects', { replace: true })
        }
      } catch (e) {
        if (cancelled) return
        clearInviteParam()
        const message = e instanceof Error ? e.message : 'Failed to accept invitation'
        showNotification(message, 'error')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [searchParams, isAuthenticated, isLoading, setSearchParams, showNotification, checkAuth, navigate])

  return null
}
